import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import {
  Sparkle, Warning, CheckCircle, Pulse, ClipboardText, ListChecks,
  Tooth, Calendar, FilePdf, Image as ImageIcon, FlowerLotus, Cigarette, Pill, Drop, Cube,
} from "@phosphor-icons/react";

const RISK = {
  green: { label: "Decorso regolare", color: "bg-[#EAFBEF] text-[#1F8B4C] border-[#1F8B4C]/40", dot: "#1F8B4C", Icon: CheckCircle },
  yellow: { label: "Da monitorare", color: "bg-[#FFFAE9] text-[#8A6D1F] border-[#8A6D1F]/40", dot: "#D9A82A", Icon: Pulse },
  red: { label: "Attenzione clinica", color: "bg-[#FAEBEB] text-[#A84A4A] border-[#A84A4A]/40", dot: "#A84A4A", Icon: Warning },
};

const FOLLOWUP_RISK_DOT = {
  normal: "#1F8B4C", monitor: "#D9A82A", contact_clinic: "#B45E2E", urgent: "#A84A4A",
};

const HEALING_TREND = {
  improving: "In miglioramento",
  stable: "Stabile",
  worsening: "In peggioramento",
  insufficient_data: "Dati insufficienti",
};

const ADHERENCE = {
  good: "Buona",
  partial: "Parziale",
  poor: "Scarsa",
  unknown: "Non valutabile",
};

export default function DigitalTwinTab({ patient }) {
  const pid = patient.id;
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busySyn, setBusySyn] = useState(false);
  const [busyReport, setBusyReport] = useState(false);
  const [synthesis, setSynthesis] = useState(null);
  const [report, setReport] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/patients/${pid}/digital-twin`);
      setData(data);
      setSynthesis(data.last_synthesis?.data || null);
      setReport(data.last_visit_report?.data || null);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const generateSynthesis = async () => {
    setBusySyn(true); setError("");
    try {
      const { data } = await api.post(`/patients/${pid}/digital-twin/synthesis`);
      setSynthesis(data.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusySyn(false); }
  };

  const generateReport = async () => {
    setBusyReport(true); setError("");
    try {
      const { data } = await api.post(`/patients/${pid}/digital-twin/visit-report`);
      setReport(data.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusyReport(false); }
  };

  const printReport = () => {
    window.print();
  };

  if (!data) return <div className="text-center text-[#56635D] py-12">Caricamento gemello digitale…</div>;

  const p = data.patient;
  return (
    <div className="space-y-6" data-testid="digital-twin-tab">
      {/* Disclaimer */}
      <div className="elev-card p-4 flex items-start gap-3 bg-brand-50 border-brand-200">
        <Sparkle size={20} weight="duotone" className="text-brand-700 mt-0.5 shrink-0" />
        <div className="text-sm text-[#1C2321]">
          <strong className="font-medium">Il sistema fornisce supporto informativo</strong> e non sostituisce la valutazione clinica del medico.
        </div>
      </div>

      {/* Surgical summary */}
      <div className="elev-card p-6" data-testid="twin-summary">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400">Riepilogo chirurgico</div>
            <h3 className="font-heading text-xl font-medium text-[#1C2321] mt-1">{p.first_name} {p.last_name}</h3>
          </div>
          <div className="text-right text-xs text-[#56635D]">
            <div>{data.followups_count} follow-up</div>
            <div>{data.files_count} file</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SumRow Icon={Calendar} label="Data intervento" value={p.intervention_date || "—"} />
          <SumRow Icon={Tooth} label="Tipo intervento" value={p.intervention_type || "—"} />
          <SumRow Icon={Cube} label="Materiali utilizzati" value={p.materials_used || "—"} />
          <SumRow Icon={FlowerLotus} label="Rigenerazione ossea / PRP-PRF" value={extractKeyword(p, ["PRP","PRF","rigenerazione","membrana","osso"]) || "—"} />
        </div>

        {(p.implants || []).length > 0 && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-[0.18em] text-brand-400 mb-2">Impianti</div>
            <ul className="space-y-1">
              {p.implants.map((im, i) => (
                <li key={i} className="text-sm text-[#1C2321]">
                  <span className="font-medium">{im.brand || "-"} {im.model}</span>
                  <span className="text-[#56635D]"> · Ø{im.diameter || "-"} × L{im.length || "-"} · Lotto {im.lot || "-"}{im.position ? ` · pos. ${im.position}` : ""}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(p.medications || []).length > 0 && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-[0.18em] text-brand-400 mb-2 flex items-center gap-2"><Pill size={14} weight="duotone" /> Farmaci prescritti</div>
            <ul className="space-y-1">
              {p.medications.map((m, i) => (
                <li key={i} className="text-sm text-[#1C2321]">
                  <span className="font-medium">{m.name}</span>
                  <span className="text-[#56635D]"> · {m.dosage} {m.frequency} {m.duration ? `per ${m.duration}` : ""}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="elev-card p-6">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-4">Azioni AI</div>
        <div className="flex flex-col sm:flex-row gap-3 sm:flex-wrap">
          <button onClick={generateSynthesis} disabled={busySyn} data-testid="twin-generate-synthesis" className="inline-flex items-center justify-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-5 py-3 rounded-lg disabled:opacity-60 whitespace-nowrap">
            <Sparkle size={18} weight="duotone" className="shrink-0" /> {busySyn ? "Generazione AI…" : "Genera sintesi clinica AI"}
          </button>
          <button onClick={generateReport} disabled={busyReport} data-testid="twin-generate-visit-report" className="inline-flex items-center justify-center gap-2 bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB] px-5 py-3 rounded-lg disabled:opacity-60 whitespace-nowrap">
            <ClipboardText size={18} weight="duotone" className="shrink-0" /> {busyReport ? "Generazione AI…" : "Prepara visita di controllo"}
          </button>
        </div>
        {error && <div className="mt-3 text-sm text-[#A84A4A] bg-[#A84A4A]/10 rounded-lg px-3 py-2" data-testid="twin-error">{error}</div>}
      </div>

      {/* Risk profile + synthesis */}
      {synthesis && (
        <div className="space-y-4 fade-up" data-testid="twin-synthesis">
          <RiskCard synthesis={synthesis} />
          <div className="elev-card p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-3">Sintesi clinica</div>
            <p className="text-[#1C2321] leading-relaxed whitespace-pre-line">{synthesis.summary}</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
              <Card label="Trend di guarigione" value={HEALING_TREND[synthesis.healing_trend] || synthesis.healing_trend} />
              <Card label="Aderenza terapia" value={ADHERENCE[synthesis.adherence] || synthesis.adherence} />
              <Card label="Impatto fumo" value={synthesis.smoking_impact || "—"} icon={<Cigarette size={14} weight="duotone" />} />
            </div>

            {(synthesis.risk_factors || []).length > 0 && (
              <div className="mt-5">
                <div className="text-xs uppercase tracking-[0.18em] text-brand-400 mb-2">Fattori di rischio</div>
                <div className="flex flex-wrap gap-1.5">
                  {synthesis.risk_factors.map((f, i) => (
                    <span key={i} className="text-xs bg-[#F0F0EE] text-[#56635D] px-2.5 py-1 rounded-full">{f}</span>
                  ))}
                </div>
              </div>
            )}

            {synthesis.suggested_next_action && (
              <div className="mt-5 elev-card p-4 bg-brand-50 border-brand-200">
                <div className="text-xs uppercase tracking-[0.18em] text-brand-700 mb-1">Azione consigliata</div>
                <div className="text-[#1C2321]">{synthesis.suggested_next_action}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pre-visit report */}
      {report && (
        <div className="elev-card p-6 fade-up" data-testid="twin-visit-report">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-brand-400">Dossier pre-visita</div>
              <h3 className="font-heading text-xl font-medium text-[#1C2321] mt-1">Visita di controllo</h3>
            </div>
            <button onClick={printReport} className="text-xs px-3 py-1.5 rounded-md bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB]">Stampa</button>
          </div>

          <ReportSection title="Cosa è stato fatto" body={report.what_done} />
          <ReportSection title="Sintomi riferiti" body={report.symptoms_reported} />
          <ReportSection title="Foto / file caricati" body={report.photos_summary} />
          <ReportSection title="Aderenza ai farmaci" body={report.medication_adherence} />

          {(report.ai_history || []).length > 0 && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-[0.18em] text-brand-400 mb-2">Storico AI follow-up</div>
              <ul className="space-y-1.5">
                {report.ai_history.map((h, i) => (
                  <li key={i} className="text-sm text-[#1C2321] flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ background: FOLLOWUP_RISK_DOT[h.risk] || "#56635D" }} />
                    <span className="font-mono text-xs text-[#56635D] w-16">Giorno {h.day}</span>
                    <span className="flex-1">{h.note}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(report.questions_to_ask || []).length > 0 && (
            <ChecklistBlock title="Domande da porre al paziente" items={report.questions_to_ask} Icon={ListChecks} />
          )}
          {(report.clinical_checks || []).length > 0 && (
            <ChecklistBlock title="Controlli clinici raccomandati" items={report.clinical_checks} Icon={Pulse} />
          )}
        </div>
      )}

      {/* Healing timeline */}
      <div className="elev-card p-6" data-testid="twin-timeline">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-1">Linea del tempo</div>
        <h3 className="font-heading text-lg font-medium text-[#1C2321] mb-5">Andamento guarigione</h3>
        {data.timeline.length === 0 ? (
          <div className="text-sm text-[#56635D]">Nessun evento da mostrare. Imposta la data intervento per generare la timeline automatica.</div>
        ) : (
          <ol className="relative border-l-2 border-brand-100 ml-2 space-y-5">
            {data.timeline.map((t, i) => <TimelinePoint key={i} point={t} />)}
          </ol>
        )}
      </div>
    </div>
  );
}

function extractKeyword(patient, keywords) {
  const blob = [patient.materials_used, patient.intervention_details, patient.notes].filter(Boolean).join(" ");
  if (!blob) return "";
  const found = keywords.filter((k) => new RegExp(k, "i").test(blob));
  return found.length ? `Rilevato: ${found.join(", ")}` : "";
}

function SumRow({ Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 shrink-0 rounded-md bg-brand-50 flex items-center justify-center text-brand-700">
        <Icon size={14} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-brand-400">{label}</div>
        <div className="text-sm text-[#1C2321] truncate">{value}</div>
      </div>
    </div>
  );
}

function Card({ label, value, icon }) {
  return (
    <div className="border border-[#E6E8E6] rounded-lg p-3 bg-[#FAFAF8]">
      <div className="text-[10px] uppercase tracking-[0.18em] text-brand-400 flex items-center gap-1.5">{icon}{label}</div>
      <div className="text-sm text-[#1C2321] mt-1 font-medium">{value}</div>
    </div>
  );
}

function RiskCard({ synthesis }) {
  const r = RISK[synthesis.risk_profile] || RISK.yellow;
  const Icon = r.Icon;
  return (
    <div className={`rounded-xl border-2 p-5 ${r.color}`} data-testid="twin-risk-profile">
      <div className="flex items-start gap-3">
        <Icon size={28} weight="duotone" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">Profilo di rischio</div>
          <div className="font-heading text-2xl font-medium mt-1">{r.label}</div>
          {(synthesis.red_flags || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {synthesis.red_flags.map((f, i) => (
                <span key={i} className="text-[10px] font-mono px-2 py-1 rounded-full bg-white/60 border border-current">{f}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReportSection({ title, body }) {
  if (!body) return null;
  return (
    <div className="mb-4">
      <div className="text-xs uppercase tracking-[0.18em] text-brand-400 mb-1">{title}</div>
      <p className="text-sm text-[#1C2321] whitespace-pre-line leading-relaxed">{body}</p>
    </div>
  );
}

function ChecklistBlock({ title, items, Icon }) {
  return (
    <div className="mt-4">
      <div className="text-xs uppercase tracking-[0.18em] text-brand-400 mb-2 flex items-center gap-1.5">{Icon ? <Icon size={14} weight="duotone" /> : null}{title}</div>
      <ul className="space-y-1.5">
        {items.map((q, i) => (
          <li key={i} className="text-sm text-[#1C2321] flex items-start gap-2">
            <span className="text-brand-400 mt-1">•</span>
            <span>{q}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TimelinePoint({ point }) {
  const isSurgery = point.kind === "surgery";
  const isFollowup = point.kind === "followup" || (point.followup);
  const ai = point.followup?.ai;
  const dot = isSurgery ? "#2C4A3B" : isFollowup ? (FOLLOWUP_RISK_DOT[ai?.risk] || "#829E8E") : "#829E8E";
  return (
    <li className="pl-6 relative">
      <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full border-2 border-white" style={{ background: dot }} />
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-heading font-medium text-[#1C2321]">{point.label}</span>
        {point.date && <span className="text-xs text-[#56635D] font-mono">{point.date}</span>}
      </div>
      {isFollowup && point.followup && (
        <div className="mt-1.5 text-sm text-[#56635D]">
          dolore {point.followup.inputs?.pain_level}/10 ·{" "}
          gonfiore {point.followup.inputs?.swelling} ·{" "}
          sanguinamento {point.followup.inputs?.bleeding}
          {ai?.risk && <span className="ml-2 text-xs font-medium px-1.5 py-0.5 rounded" style={{ background: `${FOLLOWUP_RISK_DOT[ai.risk]}1A`, color: FOLLOWUP_RISK_DOT[ai.risk] }}>{ai.risk}</span>}
        </div>
      )}
      {point.kind === "photo" && (
        <div className="mt-1 text-xs text-[#56635D] flex items-center gap-1.5">
          <ImageIcon size={12} weight="duotone" /> {point.category} · {point.label.replace("File: ", "")}
        </div>
      )}
    </li>
  );
}
